import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ArrowLeft, MessageSquare, CheckCircle2, Clock, Send, Award,
  AlertCircle, X, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { THREAD_CATEGORIES, THREAD_TAGS } from '@/data/forumData';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  useForumThread, useForumResponses, useCreateResponse, useMarkSolution,
} from '@/hooks/useForum';

export default function ThreadDetailPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const { user } = useAuth();
  const { data: thread, isLoading: threadLoading } = useForumThread(threadId);
  const { data: responses, isLoading: responsesLoading } = useForumResponses(threadId);
  const createResponse = useCreateResponse();
  const markSolution = useMarkSolution();

  const [newResponse, setNewResponse] = useState('');

  const solutionResponse = useMemo(
    () => responses?.find((r) => r.is_solution),
    [responses],
  );

  const isThreadAuthor = thread?.author_id === user?.id;

  if (threadLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-16 h-16 text-muted-foreground/50 mb-4" />
        <h2 className="text-xl font-semibold">Post no encontrado</h2>
        <p className="text-muted-foreground mt-1">El post que buscas no existe o ha sido eliminado</p>
        <Button asChild className="mt-6">
          <Link to="/forum"><ArrowLeft className="w-4 h-4 mr-2" />Volver a la comunidad</Link>
        </Button>
      </div>
    );
  }

  const handleSubmitResponse = () => {
    if (!newResponse.trim() || !thread) return;
    createResponse.mutate(
      { threadId: thread.id, content: newResponse },
      {
        onSuccess: () => {
          setNewResponse('');
          toast.success('Respuesta publicada');
        },
      },
    );
  };

  const handleMarkAsSolution = (responseId: string) => {
    markSolution.mutate(
      { responseId, threadId: thread.id, mark: true },
      { onSuccess: () => toast.success('Respuesta marcada como útil') },
    );
  };

  const handleRemoveSolution = (responseId: string) => {
    markSolution.mutate(
      { responseId, threadId: thread.id, mark: false },
      { onSuccess: () => toast.success('Marca removida') },
    );
  };

  const category = thread.category as keyof typeof THREAD_CATEGORIES;

  const getStatusBadge = () => {
    if (thread.status === 'closed') {
      return <Badge className="bg-muted text-muted-foreground border-border gap-1"><X className="w-3.5 h-3.5" />Cerrado</Badge>;
    }
    return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-200 gap-1"><Clock className="w-3.5 h-3.5" />Activo</Badge>;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Button variant="ghost" size="sm" asChild className="gap-2">
        <Link to="/forum"><ArrowLeft className="w-4 h-4" />Volver a la comunidad</Link>
      </Button>

      {/* Thread Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start gap-4">
            <Avatar className="w-12 h-12">
              <AvatarImage src={thread.author.avatar_url || undefined} />
              <AvatarFallback>{thread.author.full_name?.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">{thread.title}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="text-sm text-muted-foreground">Publicado por <span className="font-medium text-foreground">{thread.author.full_name}</span></span>
                <span className="text-muted-foreground">•</span>
                <span className="text-sm text-muted-foreground">{thread.author.company || ''}</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-sm text-muted-foreground">{formatDistanceToNow(parseISO(thread.created_at), { addSuffix: true, locale: es })}</span>
              </div>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-foreground whitespace-pre-wrap leading-relaxed">{thread.description}</p>
          <div className="flex flex-wrap gap-2 pt-2">
            {THREAD_CATEGORIES[category] && (
              <Badge variant="outline" className={cn(THREAD_CATEGORIES[category].color)}>
                {THREAD_CATEGORIES[category].icon} {THREAD_CATEGORIES[category].label}
              </Badge>
            )}
            {(thread.tags || []).map((tag) => {
              const tagKey = tag as keyof typeof THREAD_TAGS;
              return (
                <Badge key={tag} variant="outline" className={cn(THREAD_TAGS[tagKey]?.color)}>
                  {THREAD_TAGS[tagKey]?.label || tag}
                </Badge>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Solution highlight */}
      {solutionResponse && (
        <Card className="border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-emerald-600"><Award className="w-5 h-5" /><span className="font-semibold">Solución aceptada</span></div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Avatar className="w-10 h-10">
                <AvatarImage src={solutionResponse.author.avatar_url || undefined} />
                <AvatarFallback>{solutionResponse.author.full_name?.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{solutionResponse.author.full_name}</span>
                  <span className="text-sm text-muted-foreground">{formatDistanceToNow(parseISO(solutionResponse.created_at), { addSuffix: true, locale: es })}</span>
                </div>
                <p className="text-foreground mt-2 whitespace-pre-wrap">{solutionResponse.content}</p>
                {isThreadAuthor && (
                  <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" onClick={() => handleRemoveSolution(solutionResponse.id)}>
                    Quitar como solución
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Responses Section */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">
            {(responses || []).length} {(responses || []).length === 1 ? 'Respuesta' : 'Respuestas'}
          </h2>
        </div>

        {responsesLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            {(responses || []).filter((r) => !r.is_solution).map((response) => (
              <Card key={response.id} className="relative">
                <CardContent className="pt-4">
                  <div className="flex gap-4">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={response.author.avatar_url || undefined} />
                      <AvatarFallback>{response.author.full_name?.split(' ').map((n) => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{response.author.full_name}</span>
                          <span className="text-xs text-muted-foreground">{response.author.company || ''}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">{formatDistanceToNow(parseISO(response.created_at), { addSuffix: true, locale: es })}</span>
                        </div>
                        {isThreadAuthor && thread.status !== 'closed' && !solutionResponse && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="gap-1.5"><CheckCircle2 className="w-4 h-4" />Marcar como solución</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Marcar como solución?</AlertDialogTitle>
                                <AlertDialogDescription>Esta respuesta se destacará como la solución al problema.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleMarkAsSolution(response.id)}>Confirmar</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                      <p className="text-foreground mt-2 whitespace-pre-wrap leading-relaxed">{response.content}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Reply Form */}
      {thread.status !== 'closed' ? (
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-4">
              <div className="flex-1 space-y-3">
                <Textarea placeholder="Escribe tu respuesta..." rows={4} value={newResponse} onChange={(e) => setNewResponse(e.target.value)} className="resize-none" />
                <div className="flex justify-end">
                  <Button onClick={handleSubmitResponse} disabled={!newResponse.trim() || createResponse.isPending} className="gap-2">
                    <Send className="w-4 h-4" />{createResponse.isPending ? 'Publicando...' : 'Publicar respuesta'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-muted/50">
          <CardContent className="py-6 text-center text-muted-foreground">
            <X className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Este hilo está cerrado y no acepta nuevas respuestas.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
